import React from 'react';
import * as Yup from 'yup';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { FormBodyAPI } from '../../constants/fieldsTypes';
import RowDivider from '../RowDivider';
import { SettingsPageRow } from '../Settings';
import BaseTypography from '../Text/BaseTypography';

import SettingsToggleCard from '../Settings/SettingsToggleCard';
import { BaseTextField } from '../TextField';
import { GraySmall } from '../../theme/colors';
import { IconPicker } from '../Settings/ExtensionsPage';

const getSubmitValues = async () => ({});

export const fileSettingsOptions = [
  {
    id: 'always-on-visibility',
    title: 'Always-on-visibility',
    fieldName: 'alwaysOnVisibility',
    invertToggle: false,
    description:
      'Turn this on if you want to let all clients access the Files App. Turn this off if clients should only see the Files App if files have been updated.',
  },
  {
    id: 'client-file-add-disabled',
    title: 'Clients can add files',
    fieldName: 'clientFileAddDisabled',
    invertToggle: true,
    description: 'Turn this on if you want to let clients add files and links.',
  },
  {
    id: 'client-file-delete-disabled',
    title: 'Clients can delete files',
    fieldName: 'clientFileDeleteDisabled',
    invertToggle: true,
    description:
      'Turn this on if you want to let clients delete files and links.',
  },
  {
    id: 'client-folder-update-disabled',
    title: 'Clients can update folders',
    fieldName: 'clientFolderUpdateDisabled',
    invertToggle: true,
    description:
      'Turn this on if you want to let clients change the organization of a files channel (create/upload/rename/move/delete folders).',
  },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    helperText: {
      color: GraySmall,
    },
    iconPickerWrapper: {
      marginTop: theme.spacing(3.5),
      marginBottom: theme.spacing(3.5),
    },
  }),
);

export const useFileSettingsForm = () => {
  const classes = useStyles();
  const validationScheme = Yup.object().shape({
    alwaysOnVisibility: Yup.boolean(),
    clientFileDeleteDisabled: Yup.boolean(),
  });

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
  }) => (
    <>
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.container}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        helperText={touched && touched.label && errors && errors.label}
      />
      <div className={classes.iconPickerWrapper}>
        <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
        <IconPicker
          icon={values.icon || 'dashboard'}
          onIconSelected={iconName => setFieldValue('icon', iconName)}
        />
      </div>

      <SettingsPageRow hideDivider>
        <BaseTypography fontType="18Medium">Preferences</BaseTypography>
        <BaseTypography className={classes.helperText}>
          Control what client users are able to do in the Files App
        </BaseTypography>
      </SettingsPageRow>
      <RowDivider mb={0} />
      {fileSettingsOptions.map(option => (
        <SettingsToggleCard
          key={option.id}
          title={option.title}
          fieldName={option.fieldName}
          description={option.description}
          invertBooleanValue={option.invertToggle}
          checked={values[option.fieldName]}
        />
      ))}
    </>
  );

  return {
    validationScheme,
    getSubmitValues,
    FormRenderer,
  };
};
